//#region imports
import { Box } from '@mui/material';
import { Stack } from '@mui/system';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataGridPro, GridPagination } from '@mui/x-data-grid-pro';
import {
  fas,
  faCalendarPlus,
  faBan,
  faChalkboardUser,
  faHandshakeAngle,
  faStreetView,
  faScrewdriverWrench,
  faFlagCheckered,
  faSquarePlus,
  faPersonCirclePlus,
  faXmark,
  faThumbsUp,
  faSkullCrossbones,
  faPersonWalkingArrowRight,
  faCheckDouble,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons';

import customAxios from '../../utils/customAxios';
import { GlobalVariables } from '../../globalVar';
import { useTableLocale } from '../../hooks/useTableLocale';
import { useTranslationHook } from '../../hooks/UseTranslationHook';

library.add(
  fas,
  faCalendarPlus,
  faBan,
  faChalkboardUser,
  faHandshakeAngle,
  faStreetView,
  faScrewdriverWrench,
  faFlagCheckered,
  faSquarePlus,
  faPersonCirclePlus,
  faXmark,
  faThumbsUp,
  faSkullCrossbones,
  faPersonWalkingArrowRight,
  faCheckDouble,
);
//#endregion

export const TrackingVTableAxios = ({
  onVisitsDataChange,
  receiveVisitsADataTable,
  receiveVisitsNADataTable,
  receiveVisitsCDataTable,
}) => {
  const [visitsNAData, setVisitsNAData] = useState([]);
  const [visitsAData, setVisitsAData] = useState([]);
  const [visitsCData, setVisitsCData] = useState([]);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 15,
    page: 0,
  });

  const {
    dashboardDTVisitsClient,
    dashboardDTVisitsSite,
    dashboardDTVisitsVType,
    dashboardDTVisitsTech,
    noVisitsString,
    dashboardDTVisitsVC,
    dashboardDTVisitsVA,
    dashboardDTVisitsVNA,
    dashboardDTVisitsVCa,
    dashboardDTVisitsTitle,
  } = useTranslationHook();

  const endpoint = `${GlobalVariables.getURL()}techsDashboard`;

  const getData = async () => {
    setIsLoading(true);

    try {
      const response = await customAxios.get(endpoint, {
        headers: { Authorization: 'bearer ' + localStorage.getItem('token') },
      });

      const visitsNA = response.data['Servicios Sin Tecnico Asignado'].map(
        (visit) => ({
          id: visit.visit_id,
          ...visit,
        }),
      );
      const visitsA = response.data['Detalles de Servicios Asignados'].map(
        (visit) => ({
          id: visit.visit_id,
          ...visit,
        }),
      );
      const visitsC = response.data['Servicios Cancelados'].map((visit) => ({
        id: visit.visit_id,
        ...visit,
      }));

      setVisitsNAData(visitsNA);
      setVisitsAData(visitsA);
      setVisitsCData(visitsC);

      receiveVisitsADataTable(visitsA);
      receiveVisitsNADataTable(visitsNA);
      receiveVisitsCDataTable(visitsC);

      onVisitsDataChange(visitsA.concat(visitsNA));
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visitStatusMap = useMemo(() => {
    const map = new Map();
    visitsNAData.forEach((v) =>
      map.set(v.id, { isNA: true, statusText: 'isVisitNA', color: 'red' }),
    );
    visitsCData.forEach((v) =>
      map.set(v.id, { isC: true, statusText: 'isVisitCa', color: 'black' }),
    );
    visitsAData.forEach((v) => {
      const isCompleted = v.visit_status === 'Técnico ha terminado';
      map.set(v.id, {
        isA: true,
        isCompleted: isCompleted,
        statusText: isCompleted ? 'isVisitCo' : 'isVisitA',
        color: isCompleted ? 'green' : '#f5a43c',
      });
    });
    return map;
  }, [visitsAData, visitsNAData, visitsCData]);

  const dataWithStatus = useMemo(() => {
    return visitsAData.concat(visitsNAData).map((row) => {
      const statusInfo = visitStatusMap.get(row.id);
      return {
        ...row,
        status: statusInfo ? statusInfo.statusText : 'Unknown',
      };
    });
  }, [visitsAData, visitsNAData, visitStatusMap]);

  const CustomFooterTrackingV = () => (
    <Box
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      alignItems="left"
      justifyContent="flex-start"
      m={1}
      my={2}
      height="50%"
      width="50%"
    >
      <div style={{ flexBasis: '100%' }}>
        <span>
          <strong>{dashboardDTVisitsTitle}</strong>
        </span>
      </div>
      <div style={{ flexBasis: '50%' }}>
        <span>
          <FontAwesomeIcon icon={faLocationDot} color="red" />{' '}
          {dashboardDTVisitsVNA}
        </span>
      </div>
      <div style={{ flexBasis: '50%' }}>
        <span>
          <FontAwesomeIcon icon={faLocationDot} color="green" />{' '}
          {dashboardDTVisitsVC}
        </span>
      </div>
      <div style={{ flexBasis: '50%' }}>
        <span>
          <FontAwesomeIcon icon={faLocationDot} color="#f5a43c" />{' '}
          {dashboardDTVisitsVA}
        </span>
      </div>
      <div style={{ flexBasis: '50%' }}>
        <span>
          <FontAwesomeIcon icon={faLocationDot} color="black" />{' '}
          {dashboardDTVisitsVCa}
        </span>
      </div>
    </Box>
  );

  const trackingVColumns = [
    { field: 'id', headerName: 'ID', sortable: false, width: 60 },
    {
      field: 'client',
      headerName: dashboardDTVisitsClient,
      sortable: false,
      width: 150,
    },
    {
      field: 'site',
      headerName: dashboardDTVisitsSite,
      sortable: false,
      width: 200,
      renderCell: (params) => {
        const statusInfo = visitStatusMap.get(params.id);
        const fillColor = statusInfo ? statusInfo.color : 'gray';

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon
              icon={faLocationDot}
              style={{
                color: fillColor,
                fontSize: '1.5rem',
                marginRight: '5px',
              }}
            />
            <span>{params.row.site}</span>
          </div>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      hideable: false,
      sortComparator: (v1, v2) => {
        const order = { isVisitNA: 1, isVisitA: 2, isVisitCo: 3, isVisitCa: 4 };
        return (order[v1] || 99) - (order[v2] || 99);
      },
    },
    {
      field: 'tech_name',
      headerName: dashboardDTVisitsTech,
      sortable: false,
      width: 250,
    },
    {
      field: 'visit_type',
      headerName: dashboardDTVisitsVType,
      sortable: false,
      width: 180,
    },
  ].map((col) =>
    col.renderCell
      ? col
      : { ...col, renderCell: (params) => params.row[col.field] || '-' },
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    visit_type: false,
    status: false,
  });
  const locale = useTableLocale();

  return (
    <DataGridPro
      loading={isLoading}
      initialState={{
        sorting: { sortModel: [{ field: 'status', sort: 'asc' }] },
      }}
      rows={dataWithStatus}
      columns={trackingVColumns}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      onRowClick={(e) => navigate(`/visits/details/${e.id}`)}
      pagination
      paginationModel={paginationModel}
      pageSizeOptions={[5, 10, 15]}
      onPaginationModelChange={setPaginationModel}
      localeText={locale.components.MuiDataGrid.defaultProps.localeText}
      slots={{
        noRowsOverlay: () => (
          <Stack height="100%" alignItems="center" justifyContent="center">
            {noVisitsString}
          </Stack>
        ),
        footer: () => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: 'fit-content',
            }}
          >
            <CustomFooterTrackingV />
            <GridPagination />
          </div>
        ),
      }}
    />
  );
};
